import 'braid-design-system/reset';
import { ApolloProvider } from '@apollo/client';
import { VocabProvider } from '@vocab/react';
import {
  BraidProvider,
  makeLinkComponent,
  ToastProvider,
} from 'braid-design-system';
import apac from 'braid-design-system/themes/apac';
import loadable from 'sku/@loadable/component';

import createAnalytics from 'src/analytics/adapter/';
import AnalyticsProvider from 'src/analytics/AnalyticsProvider';
import { SAVED_SEARCH_ROUTE_NAME } from 'src/constants';
import getClient from 'src/getGraphqlClient';
import useExperiments from 'src/hooks/experiments/useExperiments';
import type { SkuEnvironment } from 'src/types';

import { linkContextDestructor } from '@seek/apac-candidate-header-footer';
import { Hubble, HubbleProvider } from '@seek/hubble';
import { HelmetProvider } from 'react-helmet-async';
import chaliceCountryToAudienceZone from 'src/utils/chaliceCountryToAudienceZone';
import { getLocalizedHrefHelper } from 'src/utils/customLink';
import getJobSeekerId from 'src/utils/getJobSeekerId';
import { getCountryFromSite } from 'src/utils/melwaysUtils';
import { mapLinkContextToTrackingContext } from '../HeaderFooterLinks/HeaderFooterLinks';
import AppConfigProvider, { type AppConfig } from './AppConfigProvider';
import { ExperimentsProvider } from './ExperimentContext';

const SaveSearchContainer = loadable(
  () => import('../SavedSearch/Container/Container'),
);

const RecommendationContainer = loadable(
  () => import('../Recommendations/RecommendationsContainer'),
);

type AppProps = Omit<AppConfig, 'country'>;

export default (appProps: AppProps) => {
  const {
    site,
    brand,
    environment,
    httpLink,
    route,
    experimentEnabled,
    analyticsExperimentId,
    graphqlEndpoint,
    experimentsConfig,
    experimentApiUrl,
    cookieDomain,
    language,
  } = appProps;
  const country = getCountryFromSite(site);
  const apolloClient = getClient({
    environment,
    httpLink,
    brand,
    country,
    graphqlEndpoint,
  });
  const { isExperimentReady, experiments } = useExperiments({
    experimentEnabled,
    experimentsConfig,
    experimentApiUrl,
    cookieDomain,
  });
  const analytics = createAnalytics(route, {
    experimentId: analyticsExperimentId,
    experiments,
    zone: chaliceCountryToAudienceZone(country),
    siteSection: 'discover',
    siteLanguage: language,
    isLoggedIn: true,
  });

  const localizeHref = getLocalizedHrefHelper({
    language,
    site,
  });

  const CustomLink = makeLinkComponent((originalProps, ref) => {
    const [{ href, ...restProps }, headerFooterLinkContext] =
      linkContextDestructor(originalProps);

    if (headerFooterLinkContext) {
      return (
        <a
          rel={headerFooterLinkContext?.isNoFollow ? 'nofollow' : undefined}
          ref={ref}
          href={href}
          {...restProps}
          onClick={(...arg) => {
            restProps.onClick?.(...arg);
            analytics.headerFooterLinkClicked(
              mapLinkContextToTrackingContext({
                linkContext: headerFooterLinkContext,
              }),
            );
          }}
        />
      );
    }

    const localizedHref = localizeHref(href);

    return <a ref={ref} href={localizedHref} {...restProps} />;
  });

  const Content =
    route === SAVED_SEARCH_ROUTE_NAME
      ? SaveSearchContainer
      : RecommendationContainer;

  const CustomHubbleProvider = ({ children }: { children: JSX.Element }) => {
    if (typeof window === 'undefined' || !window.document) {
      return <>{children}</>;
    }
    const isProduction =
      (appProps.environment as SkuEnvironment) === 'production';

    const hubble = new Hubble({
      appName: 'disc-static-pages',
      appVersion: window.APP_CONFIG.version || 'local',
      brand: appProps.brand,
      country,
      isProduction,
      endpoint: isProduction
        ? 'https://web.aips-sol.com/v1/events'
        : 'https://web-staging.sol-data.com/v1/events',
      loginId: getJobSeekerId() || '',
    });

    return <HubbleProvider hubble={hubble}>{children}</HubbleProvider>;
  };

  return (
    <AppConfigProvider value={{ ...appProps, country }}>
      <VocabProvider language={language} locale={country}>
        <ExperimentsProvider value={{ isExperimentReady }}>
          <AnalyticsProvider analytics={analytics}>
            <ApolloProvider client={apolloClient}>
              <CustomHubbleProvider>
                <BraidProvider theme={apac} linkComponent={CustomLink}>
                  <ToastProvider>
                    <HelmetProvider>
                      {isExperimentReady && <Content />}
                    </HelmetProvider>
                  </ToastProvider>
                </BraidProvider>
              </CustomHubbleProvider>
            </ApolloProvider>
          </AnalyticsProvider>
        </ExperimentsProvider>
      </VocabProvider>
    </AppConfigProvider>
  );
};
